import React, { useState, useRef, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars } from "@fortawesome/free-solid-svg-icons/faBars";
import { faTimes } from "@fortawesome/free-solid-svg-icons/faTimes";
import { faChevronUp } from "@fortawesome/free-solid-svg-icons/faChevronUp";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons/faChevronDown";
import ApolloLogo from "../assets/Home/logo2.jpg";
import trustpilotLogo from "../assets/trustpilot-transparent-logo.png";
import "./NavigationBar.css";

const NavigationBar: React.FC = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [isMobileProductsOpen, setIsMobileProductsOpen] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const [isMobileResourcesOpen, setIsMobileResourcesOpen] = useState(false);
  const productsRef = useRef<HTMLLIElement>(null);

  // Helper function to check if a path is active
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((prev) => !prev);
  }, []);

  const closeProductsMenu = useCallback(() => setIsProductsOpen(false), []);

  const navigate = useNavigate();

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleRedirect = useCallback(() => {
    navigate("/");
    scrollToTop();
  }, [navigate, scrollToTop]);

  const scrollToContactForm = useCallback(() => {
    if (location.pathname === "/") {
      const contactSection = document.querySelector(".get-in-touch");
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else {
      navigate("/");
      setTimeout(() => {
        const contactSection = document.querySelector(".get-in-touch");
        if (contactSection) {
          contactSection.scrollIntoView({ behavior: "smooth" });
        }
      }, 500);
    }
    setIsMobileMenuOpen(false);
  }, [location.pathname, navigate]);

  return (
    <nav className="navbar">
      <div className="navbar__top">
        <div className="navbar__logo_main">
          <div>
            <img
              src={ApolloLogo}
              alt="Logo"
              onClick={handleRedirect}
              style={{ cursor: "pointer" }}
            />
          </div>
        </div>

        <div className="navbar__center">
          <ul className="navbar__links">
            <li className={isActive("/") ? "active" : ""}>
              <Link to="/" onClick={scrollToTop}>
                Home
              </Link>
            </li>

            <li className={isActive("/about/") ? "active" : ""}>
              <Link to="/about/" onClick={scrollToTop}>
                About
              </Link>
            </li>

            <li
              className={`products-dropdown ${
                location.pathname.startsWith("/services/financial-review/") ||
                location.pathname.startsWith("/services/pension/") ||
                location.pathname.startsWith("/services/life-protection/") ||
                location.pathname.startsWith("/services/investments/")
                  ? "active"
                  : ""
              }`}
              onMouseEnter={() => setIsProductsOpen(true)}
              onMouseLeave={() => setIsProductsOpen(false)}
              ref={productsRef}
            >
              <button className="products-dropdown-toggle">
                Services
                <FontAwesomeIcon
                  icon={isProductsOpen ? faChevronUp : faChevronDown}
                  className="dropdown-icon"
                />
              </button>
              {isProductsOpen && (
                <ul className="products-dropdown-menu">
                  <li
                    className={
                      isActive("/services/financial-review/") ? "active" : ""
                    }
                  >
                    <Link
                      to="/services/financial-review/"
                      onClick={() => {
                        scrollToTop();
                        closeProductsMenu();
                      }}
                    >
                      Full Financial Review
                    </Link>
                  </li>
                  <li
                    className={isActive("/services/pension/") ? "active" : ""}
                  >
                    <Link
                      to="/services/pension/"
                      onClick={() => {
                        scrollToTop();
                        closeProductsMenu();
                      }}
                    >
                      Pension & Retirement
                    </Link>
                  </li>
                  <li
                    className={
                      isActive("/services/life-protection/") ? "active" : ""
                    }
                  >
                    <Link
                      to="/services/life-protection/"
                      onClick={() => {
                        scrollToTop();
                        closeProductsMenu();
                      }}
                    >
                      Life & Protection
                    </Link>
                  </li>
                  <li
                    className={
                      isActive("/services/investments/") ? "active" : ""
                    }
                  >
                    <Link
                      to="/services/investments/"
                      onClick={() => {
                        scrollToTop();
                        closeProductsMenu();
                      }}
                    >
                      Investments
                    </Link>
                  </li>
                </ul>
              )}
            </li>
            <li className={isActive("/faq/") ? "active" : ""}>
              <Link to="/faq/" onClick={scrollToTop}>
                FAQ's
              </Link>
            </li>
            <li className={isActive("/contact/") ? "active" : ""}>
              <Link to="/contact/" onClick={scrollToTop}>
                Contact
              </Link>
            </li>
            <li
              className={`products-dropdown ${
                location.pathname.startsWith("/resources/risk-profiler/")
                  ? "active"
                  : ""
              }`}
              onMouseEnter={() => setIsResourcesOpen(true)}
              onMouseLeave={() => setIsResourcesOpen(false)}
            >
              <button className="products-dropdown-toggle">
                Resources
                <FontAwesomeIcon
                  icon={isResourcesOpen ? faChevronUp : faChevronDown}
                  className="dropdown-icon"
                />
              </button>
              {isResourcesOpen && (
                <ul className="products-dropdown-menu">
                  <li
                    className={
                      isActive("/resources/risk-profiler/") ? "active" : ""
                    }
                  >
                    <Link
                      to="/resources/risk-profiler/"
                      onClick={() => {
                        scrollToTop();
                        setIsResourcesOpen(false);
                      }}
                    >
                      Risk Profiler
                    </Link>
                  </li>
                  <li
                    className={
                      isActive("/resources/content-hub/") ? "active" : ""
                    }
                  >
                    <Link
                      to="/resources/content-hub/"
                      onClick={() => {
                        scrollToTop();
                        setIsResourcesOpen(false);
                      }}
                    >
                      Content Hub
                    </Link>
                  </li>
                </ul>
              )}
            </li>
          </ul>
        </div>

        {/* Mobile Menu */}
        <ul className={`navbar__mobile-menu ${isMobileMenuOpen ? "open" : ""}`}>
          <li className={isActive("/") ? "active" : ""}>
            <Link
              to="/"
              onClick={() => {
                scrollToTop();
                setIsMobileMenuOpen(false);
              }}
            >
              Home
            </Link>
          </li>
          <li className={isActive("/about/") ? "active" : ""}>
            <Link
              to="/about/"
              onClick={() => {
                scrollToTop();
                setIsMobileMenuOpen(false);
              }}
            >
              About
            </Link>
          </li>
          <li className="mobile-products-wrapper">
            <button
              className={`mobile-submenu-toggle ${
                location.pathname.startsWith("/services/financial-review/") ||
                location.pathname.startsWith("/services/pension/") ||
                location.pathname.startsWith("/services/life-protection/") ||
                location.pathname.startsWith("/services/investments/")
                  ? "active"
                  : ""
              }`}
              onClick={() => setIsMobileProductsOpen(!isMobileProductsOpen)}
            >
              Services{" "}
              <FontAwesomeIcon icon={isMobileProductsOpen ? faTimes : faBars} />
            </button>
            {isMobileProductsOpen && (
              <ul className="mobile-products-dropdown">
                <li
                  className={
                    isActive("/services/financial-review/") ? "active" : ""
                  }
                >
                  <Link
                    to="/services/financial-review/"
                    onClick={() => {
                      scrollToTop();
                      setIsMobileMenuOpen(false);
                      setIsMobileProductsOpen(false);
                    }}
                  >
                    Full Financial Review
                  </Link>
                </li>
                <li className={isActive("/services/pension/") ? "active" : ""}>
                  <Link
                    to="/services/pension/"
                    onClick={() => {
                      scrollToTop();
                      setIsMobileMenuOpen(false);
                      setIsMobileProductsOpen(false);
                    }}
                  >
                    Pension & Retirement
                  </Link>
                </li>
                <li
                  className={
                    isActive("/services/life-protection/") ? "active" : ""
                  }
                >
                  <Link
                    to="/services/life-protection/"
                    onClick={() => {
                      scrollToTop();
                      setIsMobileMenuOpen(false);
                      setIsMobileProductsOpen(false);
                    }}
                  >
                    Life & Protection
                  </Link>
                </li>
                <li
                  className={isActive("/services/investments/") ? "active" : ""}
                >
                  <Link
                    to="/services/investments/"
                    onClick={() => {
                      scrollToTop();
                      setIsMobileMenuOpen(false);
                      setIsMobileProductsOpen(false);
                    }}
                  >
                    Investments
                  </Link>
                </li>
              </ul>
            )}
          </li>
          <li className={isActive("/faq/") ? "active" : ""}>
            <Link
              to="/faq/"
              onClick={() => {
                scrollToTop();
                setIsMobileMenuOpen(false);
              }}
            >
              FAQ's
            </Link>
          </li>
          <li className={isActive("/contact/") ? "active" : ""}>
            <Link
              to="/contact/"
              onClick={() => {
                scrollToTop();
                setIsMobileMenuOpen(false);
              }}
            >
              Contact
            </Link>
          </li>
          <li className="mobile-products-wrapper">
            <button
              className={`mobile-submenu-toggle ${
                location.pathname.startsWith("/resources/risk-profiler/")
                  ? "active"
                  : ""
              }`}
              onClick={() => setIsMobileResourcesOpen(!isMobileResourcesOpen)}
            >
              Resources
              <FontAwesomeIcon
                icon={isMobileResourcesOpen ? faTimes : faBars}
              />
            </button>
            {isMobileResourcesOpen && (
              <ul className="mobile-products-dropdown">
                <li
                  className={
                    isActive("/resources/risk-profiler/") ? "active" : ""
                  }
                >
                  <Link
                    to="/resources/risk-profiler/"
                    onClick={() => {
                      scrollToTop();
                      setIsMobileMenuOpen(false);
                      setIsMobileResourcesOpen(false);
                    }}
                  >
                    Risk Profiler
                  </Link>
                </li>
                <li
                  className={
                    isActive("/resources/content-hub/") ? "active" : ""
                  }
                >
                  <Link
                    to="/resources/content-hub/"
                    onClick={() => {
                      scrollToTop();
                      setIsMobileMenuOpen(false);
                      setIsMobileResourcesOpen(false);
                    }}
                  >
                    Content Hub
                  </Link>
                </li>
              </ul>
            )}
          </li>
        </ul>

        <div className="navbar__logo">
          <button className="get-in-touch-button" onClick={scrollToContactForm}>
            GET IN TOUCH
          </button>
        </div>

        <div className="navbar__mobile-wrapper">
          <button className="mobile-menu-button" onClick={toggleMobileMenu}>
            <FontAwesomeIcon icon={isMobileMenuOpen ? faTimes : faBars} />
          </button>
        </div>
      </div>
      <div className="trustpilot-banner-inside">
        <span className="trustpilot-banner__text">
          <span className="glowing-star">🌟</span> Rated Excellent on
          <img
            src={trustpilotLogo}
            alt="Trustpilot"
            className="trustpilot-banner__logo"
          />{" "}
          Trustpilot
        </span>
      </div>
    </nav>
  );
};

export default React.memo(NavigationBar);
